
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IHttpRestService } from '@yourcause/common/http';
import { filter, lastValueFrom, map, Observable } from 'rxjs';
export const Standard_Number_Of_Retries = 2;

@Injectable({ providedIn: 'root' })
export class HttpRestService implements IHttpRestService {

  constructor (
    private http: HttpClient
  ) { }

  async get <T = any> (
    url: string,
    headers?: any,
    numberOfTimesToRetry = 0
  ): Promise <T> {
    if (numberOfTimesToRetry > 0) {
      return this.handleRetries('get', url, null, headers, numberOfTimesToRetry);
    }

    return this.makeRequest(url, 'get', null, headers);
  }



  async post <T = any, B = any> (
    url: string,
    data: B,
    headers?: any,
    numberOfTimesToRetry = 0
  ): Promise<T> {
    if (numberOfTimesToRetry > 0) {
      return this.handleRetries('post', url, data, headers, numberOfTimesToRetry);
    }

    return this.makeRequest(url, 'post', data, headers);
  }

  /**
   * Handles retries for HTTP requests.
   * 
   * @param type: Type to retry - 'get' or 'post'
   * @param url: URL to make the request to
   * @param data: Data to send with the request (for 'post' only)
   * @param headers: Headers to include in the request
   * @param numberOfTimesToRetry: Number of times to retry the request if it fails
   * @returns the response from the server, or throws an error if the request fails after retries
   */
  async handleRetries<T = any, B = any> (
    type: 'get'|'post',
    url: string,
    data: B = null,
    headers?: any,
    numberOfTimesToRetry = 0
  ) {
    let retryCount = 0;
    try {
      const response = await this.makeRequest(url, type, data, headers);

      return <T>response;
    } catch (error) {
      // Exclude bad request, not found, and internal server errors which will not be resolved by retrying
      const isValidStatus = !error || ![400, 404, 500].includes((error as any)?.status);
      if (isValidStatus) {
        if (retryCount <= numberOfTimesToRetry) {
          retryCount++;
          const retryTimes = numberOfTimesToRetry - retryCount;
          switch (type) {
            case 'get':
              return this.get<T>(url, headers, retryTimes);
            case 'post':
              return this.post<T>(url, data, headers, retryTimes);
          }
        }
      }

      throw error;
    }
  }

  async postFile<T = any> (
    url: string,
    fileToUpload: File|Blob|string,
    paramName = 'file',
    fileName?: string
  ) {
    const { request } = this.setupFormData(fileToUpload, fileName, paramName, url);

    return lastValueFrom(this.http.request(request).pipe(
      filter(event => event instanceof HttpResponse),
      map(event => (<HttpResponse<T>>event).body)));
  }

  private setupFormData (
    file: string | File | Blob,
    fileName: string,
    paramName: string,
    url: string,
    responseType: 'text'|'arraybuffer'|'blob'|'json' = 'json'
  ) {
    const formData = new FormData();
    if (typeof file === 'string') {
      file = new File([new Blob([file])], fileName || 'upload');
    }
    if (!(file instanceof File) && fileName) {
      formData.append(paramName, file, fileName);
    } else {
      formData.append(paramName, file);
    }
    const request = new HttpRequest('POST', url, formData, {
      reportProgress: true,
      headers: new HttpHeaders({ 'ngsw-bypass': 'true' }),
      responseType
    });

    return { formData, request, file };
  }

  getBlob (url: string): Promise<Blob> {
    return lastValueFrom(this.http.get(url, { responseType: 'blob' }));
  }

  delete <T = any> (url: string, headers?: any): Promise<T> {
    return this.makeRequest(url, 'delete', null, headers);
  }

  protected makeRequest <T, B> (
    url: string,
    method: 'get'|'post'|'put'|'delete',
    data: B,
    inputHeaders?: any
  ): Promise<T> {
    let observable: Observable<T>;
    switch (method) {
      case 'get':
        observable = this.http.get<T>(url, {
          headers: inputHeaders
        });
        break;
      case 'post':
        observable = this.http.post<T>(url, data, {
          headers: inputHeaders
        });
        break;
      case 'put':
        observable = this.http.put<T>(url, data, {
          headers: inputHeaders
        });
        break;
      case 'delete':
        observable = this.http.delete<T>(url, {
          headers: inputHeaders
        });
        break;
    }

    return lastValueFrom(observable);
  }
}
